import { isFalsy } from 'utility-types';
import fetchRequest from '@/api/api';
import type { AppUserStoreResponse, BaseApiResponse, BaseApiResult, OrganizationNameChange, OrganizationUsersResponse } from '@/interfaces/service/api.interface';
import type { OrganizationRole, UserStatus } from '@/interfaces/service/organization.interface';
import { ACCEPT_INVITATIONS_PATTERN, GET_INVITATION_INFOS_PATTERN, convertPathVariable } from '@/constants/path.constants';
import { checkHttpRequestResult } from '@/utils/api.util';

// 팀 회원 응답 타입
export interface TeamUsersResponse {
  type: string;
  id: string;
  teamAppUserCount: number;
  teamAppUsers: {
    appUserId: number;
    email: string;
    name: string;
    phone: string | null;
    organizationRole: string;
    organizationAppUserStatus: string;
    teamInfo: {
      teamId: string;
      teamName: string;
      permissionWriting: boolean;
      permissionReading: boolean;
    }[];
  }[];
}

/**
 * 조직명을 변경합니다.
 * @param organizationId 조직 ID
 * @param newName 새로운 조직명
 */
export const updateOrganizationName = async (organizationId: string, newOrganizationName: string) => {
  try {
    const res: BaseApiResponse<OrganizationNameChange> = await fetchRequest({
      endpoint: `/api/v1/organization/${organizationId}`,
      method: 'PATCH',
      body: {
        organizationName: newOrganizationName
      }
    });
    const result = checkHttpRequestResult(res);
    return result;
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '조직명 변경 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 새로운 조직을 생성합니다.
 * @param teamAuthId 네이버 인증 후 받은 팀 인증 ID
 * @param storeId 병원 ID
 * @param organizationName 조직 이름
 * @returns 성공 시 생성된 조직 정보, 실패 시 에러 메시지
 */
export const createOrganization = async (teamAuthId: string, storeId: number, organizationName: string) => {
  try {
    const res: BaseApiResponse<OrganizationNameChange> = await fetchRequest({
      endpoint: '/api/v1/organizations',
      method: 'POST',
      body: {
        teamAuthId,
        storeId,
        organizationName
      }
    });

    const result = checkHttpRequestResult(res);
    return result;
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '조직 생성 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 조직에 새 팀을 등록합니다.
 * @param organizationId 조직 ID
 * @param teamAuthId 네이버 인증 후 받은 팀 인증 ID
 * @param storeId 병원 ID
 * @returns 성공 시 등록된 팀 정보(teamId), 실패 시 에러 메시지
 */
export const registerTeamToOrganization = async (organizationId: string, teamAuthId: string, storeId: number | string): Promise<BaseApiResult<{ teamId: string }>> => {
  try {
    const res: BaseApiResponse<{ teamId: string }> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/teams`,
      method: 'POST',
      body: {
        teamAuthId,
        storeId: typeof storeId === 'number' ? storeId.toString() : storeId
      }
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '팀 등록 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 조직에 유저를 초대합니다.
 * @param organizationId 조직 ID
 * @param email 초대할 유저의 이메일
 * @param role 유저의 역할 ("ORGANIZATION_ADMIN" 또는 "ORGANIZATION_USER")
 * @param teamPermissionList 팀별 권한 목록
 * @returns 성공 시 초대된 유저 정보, 실패 시 에러 메시지
 */
export interface TeamPermission {
  teamId: string;
  permissionWriting: boolean;
  permissionReading: boolean;
}

export interface InviteUserResponse {
  organizationId: string;
  appUserId: number;
  email: string;
  role: string;
  teamPermissionList: TeamPermission[];
}

export const inviteUserToOrganization = async (organizationId: string, email: string, role: string, teamPermissionList: TeamPermission[]): Promise<BaseApiResult<InviteUserResponse>> => {
  try {
    const res: BaseApiResponse<InviteUserResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/invitations`,
      method: 'POST',
      body: {
        organizationId,
        email,
        role,
        teamPermissionList
      }
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '유저 초대 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 조직에서 팀을 삭제합니다.
 * @param organizationId 조직 ID
 * @param teamId 삭제할 팀 ID
 * @returns 성공 시 삭제된 팀 정보, 실패 시 에러 메시지
 */
export const deleteTeamFromOrganization = async (
  organizationId: string,
  teamId: string
): Promise<
  BaseApiResult<{
    teamId: string;
    storeName: string;
    storeDetail: string;
  }>
> => {
  try {
    const res: BaseApiResponse<{
      teamId: string;
      storeName: string;
      storeDetail: string;
    }> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/teams/${teamId}`,
      method: 'DELETE'
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '팀 삭제 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 조직의 회원 목록을 조회합니다.
 * @param organizationId 조직 ID
 * @returns 성공 시 조직 회원 목록 정보, 실패 시 에러 메시지
 */
export const getOrganizationUsers = async (organizationId: string): Promise<BaseApiResult<OrganizationUsersResponse>> => {
  try {
    const res: BaseApiResponse<OrganizationUsersResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/users`,
      method: 'GET'
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '조직 회원 목록 조회 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 조직 내 특정 팀의 회원 목록을 조회합니다.
 * @param organizationId 조직 ID
 * @param teamId 팀 ID
 * @returns 성공 시 팀 회원 목록 정보, 실패 시 에러 메시지
 */
export const getTeamUsers = async (organizationId: string, teamId: string): Promise<BaseApiResult<TeamUsersResponse>> => {
  try {
    const res: BaseApiResponse<TeamUsersResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/teams/${teamId}/users`,
      method: 'GET'
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '팀 회원 목록 조회 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 조직에서 회원을 삭제합니다.
 * @param organizationId 조직 ID
 * @param targetAppUserId 삭제할 회원의 AppUser ID
 * @returns 성공 시 삭제된 회원 정보, 실패 시 에러 메시지
 */
export interface DeleteOrganizationUserResponse {
  organizationId: string;
  requestAppUserId: number;
  targetAppUserId: number;
  deletedOrganizationAppUserCount: number;
  deletedTeamAppUserCount: number;
}

export const deleteOrganizationUser = async (organizationId: string, targetAppUserId: number): Promise<BaseApiResult<DeleteOrganizationUserResponse>> => {
  try {
    const res: BaseApiResponse<DeleteOrganizationUserResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/users/${targetAppUserId}`,
      method: 'DELETE'
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '조직 회원 삭제 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 조직 내 회원의 권한을 수정합니다.
 * @param organizationId 조직 ID
 * @param targetAppUserId 권한을 수정할 회원의 AppUser ID
 * @param organizationRole 새로운 조직 권한 ("ORGANIZATION_USER" 또는 "ORGANIZATION_ADMIN")
 * @returns 성공 시 업데이트된 회원 정보, 실패 시 에러 메시지
 */
export interface UpdateUserRoleResponse {
  organizationId: string;
  appUserId: number;
  organizationRole: string;
}

export const updateOrganizationUserRole = async (organizationId: string, targetAppUserId: number, organizationRole: string): Promise<BaseApiResult<UpdateUserRoleResponse>> => {
  try {
    const res: BaseApiResponse<UpdateUserRoleResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/users/${targetAppUserId}/role`,
      method: 'PATCH',
      body: {
        organizationRole
      }
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '회원 권한 수정 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 조직 내 회원의 팀 권한을 업데이트합니다.
 * @param organizationId 조직 ID
 * @param targetAppUserId 권한을 수정할 회원의 AppUser ID
 * @param teamPermissionList 팀별 권한 목록
 * @returns 성공 시 업데이트된 회원 정보, 실패 시 에러 메시지
 */
export interface UpdateUserTeamPermissionsResponse {
  organizationId: string;
  appUserId: number;
  teamPermissionList: TeamPermission[];
}

export const updateOrganizationUserTeamPermissions = async (
  organizationId: string,
  targetAppUserId: number,
  teamPermissionList: TeamPermission[]
): Promise<BaseApiResult<UpdateUserTeamPermissionsResponse>> => {
  try {
    const res: BaseApiResponse<UpdateUserTeamPermissionsResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/teams/users/${targetAppUserId}/permission`,
      method: 'PATCH',
      body: teamPermissionList
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '회원 팀 권한 수정 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 팀에 유저를 등록합니다. (기존에 팀과 관계가 없는 유저를 팀에 추가)
 * @param organizationId 조직 ID
 * @param teamId 팀 ID
 * @param targetUserId 등록할 회원의 AppUser ID
 * @param permissionWriting 쓰기 권한 여부
 * @param permissionReading 읽기 권한 여부
 * @returns 성공 시 등록된 팀 유저 정보, 실패 시 에러 메시지
 */
export interface RegisterTeamUserResponse {
  teamAppUserId: string;
  organizationAppUserId: string;
  teamId: string;
  appUserId: number;
  permissionWriting: boolean;
  permissionReading: boolean;
}

export const registerTeamUser = async (
  organizationId: string,
  teamId: string,
  targetUserId: number,
  permissionWriting: boolean,
  permissionReading: boolean
): Promise<BaseApiResult<RegisterTeamUserResponse>> => {
  try {
    const res: BaseApiResponse<RegisterTeamUserResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/teams/${teamId}/users/${targetUserId}`,
      method: 'POST',
      body: {
        permissionWriting,
        permissionReading
      }
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '팀 유저 등록 중 오류가 발생했습니다.'
    };
  }
};

/**
 * 팀에서 유저를 삭제합니다.
 * @param organizationId 조직 ID
 * @param teamId 팀 ID
 * @param targetUserId 삭제할 회원의 AppUser ID
 * @returns 성공 시 삭제된 팀 유저 정보, 실패 시 에러 메시지
 */
export interface DeleteTeamUserResponse {
  organizationId: string;
  teamId: string;
  requestAppUserId: number;
  targetAppUserId: number;
}

export const deleteTeamUser = async (organizationId: string, teamId: string, targetUserId: number): Promise<BaseApiResult<DeleteTeamUserResponse>> => {
  try {
    const res: BaseApiResponse<DeleteTeamUserResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/teams/${teamId}/users/${targetUserId}`,
      method: 'DELETE'
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '팀 멤버 삭제 중 오류가 발생했습니다.'
    };
  }
};

export const getInvitationInfo = async (
  organizationId: string,
  invitationToken: string
): Promise<
  | BaseApiResult<{
      organizationName: string;
      appUserId: number;
      email: string;
      appUserStatus: UserStatus;
      role: OrganizationRole;
    }>
  | undefined
> => {
  try {
    const res: BaseApiResponse<{
      organizationName: string;
      appUserId: number;
      email: string;
      appUserStatus: UserStatus;
      role: OrganizationRole;
    }> = await fetchRequest({
      endpoint: convertPathVariable(GET_INVITATION_INFOS_PATTERN, {
        organizationId,
        invitationToken
      }),
      method: 'GET'
    });

    if (res && res?.data) {
      return checkHttpRequestResult(res);
    }
  } catch (error) {}
};

export const acceptInvitation = async (organizationId: string, invitationToken: string): Promise<boolean> => {
  try {
    const res: BaseApiResponse<any> = await fetchRequest({
      endpoint: convertPathVariable(ACCEPT_INVITATIONS_PATTERN, {
        organizationId,
        invitationToken
      }),
      method: 'POST'
    });

    return res && !isFalsy(res?.data);
  } catch (error) {
    return false;
  }
};

export const getTeamInfobyOgranizationId = async (organizationId: string) => {
  try {
    const res: BaseApiResponse<AppUserStoreResponse> = await fetchRequest({
      endpoint: `/api/v1/organizations/${organizationId}/teams`,
      method: 'GET'
    });

    return checkHttpRequestResult(res);
  } catch (error) {
    return {
      isSuccess: false,
      status: '500',
      message: '병원 정보 조회 중 오류가 발생했습니다.'
    };
  }
};
